import { Box, Divider, Grid, Link, makeStyles, Paper, Slide, Tooltip, Typography } from "@material-ui/core";
import { InspectionReportStatusChip } from "./components/inspectionReportStatusChip";
import { Item } from "./components/item";
import { InspectionPhotosStats } from './components/inspectionPhotosStats';
import clsx from "clsx";
import { PhotoList, PhotosAccordion, PhotosAccordionHandle } from './components/photosAccordion';
import { InspectionPhotosMap } from "./components/inspectionPhotosMap";
import { useStore } from "@roc/client-portal-shared/hooks";
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { PhotoDetail } from "./components/photoDetail";
import useRedirectUrl from "./hooks/useRedirectUrl";
import { VerticalBarLoader, Button, ConfirmationButton, SelectField } from "@roc/ui";
import { PickLocationDialog } from "./components/pickLocatonDialog";
import { MovePhotosDialog } from './components/movePhotosDialog';
import { MoveToSectionDialog } from './components/moveToSectionDialog';
import { SubmitForReviewSourceDialog } from './components/submitForReviewSourceDialog';
import { formatDate, parseDate } from '@roc/feature-utils';
import { compareDesc } from 'date-fns';
import 'swiper/swiper.min.css'

const useStyles = makeStyles(() => ({
  paper: {
    padding: "1rem",
    border: "1px solid lightgray",
    borderRadius: "20px",
  },
  photoUploadsPaper: {
    flexGrow: 1,
    overflow: "hidden",
    padding: "16px 0px",
  },
  mapContainerPaper: {
    padding: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: '600px',
    maxHeight: '60vh',
    top:0,
    position: 'sticky'
  },
  divider: {
    marginBlock: "15px",
  },
  highlightedText: {
    color: "#39BCD0",
  },
  photo: {
    width: "220px",
    height: "190px",
    objectFit: "cover",
  },
  hiddenMap: {
    position: "fixed",
    opacity: 0,
    top: -1000,
    left: -1000
  },
  selectSortBy: {
    '& .MuiOutlinedInput-input': {
      paddingTop: '8px',
      paddingBottom: '8px'
    }
  }
}));

interface InspectionReportProps {
  inspectionId: number;
  style?: CSSProperties;
  isInternalValuationPropertyPhotosContactFlow?: boolean;
  isInternalValuationInspection?: boolean;
  isManualUploadMode?: boolean;
  smallScreen?: boolean;
  externalId1?: number;
  externalId2?: number;
}

enum SortPhotosBy {
  TASKS = 'TASKS',
  DATE = 'DATE'
}

export const InspectionReport = observer(({  inspectionId, externalId1, externalId2, style, isInternalValuationPropertyPhotosContactFlow, isManualUploadMode, smallScreen }: InspectionReportProps) => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const { inspectionPhotosStore } = inspectionStore;
  const accordionRefs = useRef<{ [key: number]: PhotosAccordionHandle }>({});
  const [pickLocationOpen, setPickLocationOpen] = useState(false);
  const [movePhotosOpen, setMovePhotosOpen] = useState(false);
  const [moveToSectionOpen, setMoveToSectionOpen] = useState(false);
  const [submitForReviewSourceOpen, setSubmitForReviewSourceOpen] = useState(false);
  const { canApproveManually } = inspectionStore;
  const [sortBy, setSortBy] = useState(SortPhotosBy.TASKS);

  // isManualUploadMode (the prop) stays the single source of truth for CAPABILITY (Add Photos,
  // Submit for Review, which Move Photos dialog to open). displayAsManual additionally covers
  // DISPLAY (hide map/stats/device-location metadata/status icons) for a read-only context — like
  // the Inspections Dashboard — that never passes the prop but is showing an inspection whose
  // active RealVal photo source already resolved to MANUAL.
  const displayAsManual = isManualUploadMode || inspectionStore.activeRealValPhotoSource === 'MANUAL';
  // READY_FOR_REVIEW/COMPLETED/REJECTED are the locked statuses on the backend
  // (InspectionStatus.isInspectionLocked) — moving photos between sections, or re-submitting,
  // would 400 there, so hide the button instead of letting the user hit that error.
  const isInspectionOpen = !['READY_FOR_REVIEW', 'COMPLETED', 'REJECTED'].includes(
    inspectionStore.inspectionDetails?.inspectionStatus
  );

  //this will load the data for the map when the component is rendered
  useEffect(() => {
    inspectionStore.reset(true);
    const inspectionLoaded = !!inspectionStore.inspectionDetails;
    if (!inspectionLoaded && inspectionId) {
      inspectionStore.loadInspection(inspectionId);
    } else if (!inspectionLoaded && externalId1 && externalId2) {
      inspectionStore.loadInspectionByExternalIds(externalId1, externalId2);
    }
  }, [inspectionId, externalId1, externalId2]);

    const inspectionDetails = inspectionStore.inspectionDetails;
    const inspectionTasks = inspectionStore.inspectionTasks || [];
    const inspectionPhotos = inspectionPhotosStore.inspectionPhotos;
    const isPhotoSelected = !!inspectionStore.inspectionPhotoSelected;

  const loanUrl = useRedirectUrl({ type: 'loan', id: inspectionDetails?.externalId1 });

    const handleLoanLinkClick = useCallback(() => {
      window.open(loanUrl);
    }, [loanUrl]);

    const onInspectionPhotoClickHandler = (inspectionPhotoId: number) => {
    const inspectionPhotoClicked = inspectionPhotos.find(photo => photo.inspectionPhotoId === inspectionPhotoId);
      if (inspectionPhotoClicked) {
      accordionRefs.current[inspectionPhotoClicked.inspectionTaskId]?.triggerAnimation(inspectionPhotoId);
      }
  }

    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          gridGap="1rem"
        padding={smallScreen ? '8px' : "2rem"}
          minHeight={'400px'}
          style={style}
        >
          {/* Title */}
          <Box display="flex" flexDirection="column">
            <Box
              display="flex"
              justifyContent="start"
              alignItems="center"
              gridGap="1rem"
            >
              <Typography variant="h4">Inspection Report</Typography>
              <InspectionReportStatusChip
                status={inspectionDetails?.inspectionStatus}
              />
            <Item title="Location" style={{
                  flex: 1,
              alignItems: "flex-end",
              paddingTop: '8px'
            }}>
                <Typography variant="body1">
                  {inspectionDetails?.propertyAddress}
                </Typography>
                {canApproveManually && !displayAsManual && (
                  <Link
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => setPickLocationOpen(true)}
                  >
                    Edit Location Coordinates
                  </Link>
                )}
              </Item>
            </Box>
            <Typography variant="body2">
              Last updated on: {inspectionDetails?.lastUpdatedDate}
            </Typography>
          </Box>

          {/* Body */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap="1rem"
            >
                {/* Report info */}
                <Paper className={classes.paper} elevation={0}>
                  <Box display="flex" gridGap="3rem">
                    <Item title="Loan">
                      <Box display="flex" gridGap="8px">
                        <Typography variant="body1">ID: </Typography>
                        <Link
                          className={classes.highlightedText}
                          onClick={handleLoanLinkClick}
                        >
                          {inspectionDetails?.externalId1}
                        </Link>
                      </Box>
                    </Item>
                    <Item
                      title={`${
                        isInternalValuationPropertyPhotosContactFlow
                          ? 'Collateral'
                          : 'Draw'
                      }`}
                    >
                      <Box display="flex" gridGap="8px">
                        <Typography variant="body1">ID: </Typography>
                        <Typography variant="body1">
                          {inspectionDetails?.externalId2}
                        </Typography>
                      </Box>
                    </Item>
                    <InspectionPhotosStats
                      totalPhotos={inspectionPhotosStore.totalPhotos}
                      okPhotos={displayAsManual ? 0 : inspectionPhotosStore.automaticallyValidatedPhotos}
                      photosWithIssues={displayAsManual ? 0 : inspectionPhotosStore.photosWithIssues}
                      manuallyApprovedPhotos={displayAsManual ? 0 : inspectionPhotosStore.manuallyApprovedPhotos}
                      showEmptyResult
                    />
                    {inspectionDetails?.photoSource && (
                      <Item title="Source">
                        <Typography variant="body1">
                          {inspectionDetails.photoSource === 'OTHER' ? (
                            <Tooltip title={inspectionDetails.photoSourceOther || ''}>
                              <span>
                                {(inspectionDetails.photoSourceOther || '').length > 24
                                  ? `${inspectionDetails.photoSourceOther.slice(0, 24)}…`
                                  : inspectionDetails.photoSourceOther}
                              </span>
                            </Tooltip>
                          ) : (
                            'MLS'
                          )}
                        </Typography>
                      </Item>
                    )}
                    {inspectionDetails?.photosTakenDate && (
                      <Item title="Photos taken">
                        <Typography variant="body1">
                          {formatDate(inspectionDetails.photosTakenDate, 'MM/dd/yyyy')}
                        </Typography>
                      </Item>
                    )}
                  </Box>
                </Paper>

                {/* Photo Uploads */}
                <Paper
                  className={clsx(classes.paper, classes.photoUploadsPaper)}
                  elevation={0}
                >
                <Box
                  display="flex"
                  flexDirection="column"
                  gridGap="1rem"
                >
                    <Box display="flex">
                      <Box flex={1}>
                        <Typography variant="h5" style={{ paddingLeft: 16 }}>
                          Photo Uploads
                        </Typography>
                      </Box>
                      {canApproveManually && !displayAsManual &&
                        <Box pr={2}>
                          <Button
                            testId="move-photos"
                            variant="outlined"
                            color="primary"
                            onClick={() => setMovePhotosOpen(true)}
                          >
                            Move Photos
                          </Button>
                        </Box>
                      }
                      {isManualUploadMode && isInspectionOpen &&
                        <Box pr={2}>
                          <Button
                            testId="move-photos-to-section"
                            variant="outlined"
                            color="primary"
                            onClick={() => setMoveToSectionOpen(true)}
                          >
                            Move Photos
                          </Button>
                        </Box>
                      }
                      {isManualUploadMode && isInspectionOpen && inspectionPhotosStore.totalPhotos > 0 && (
                        <Box pr={2}>
                          <Button
                            testId="submit-for-review"
                            variant="contained"
                            color="primary"
                            onClick={() => setSubmitForReviewSourceOpen(true)}
                          >
                            Submit for Review
                          </Button>
                        </Box>
                      )}
                      {canApproveManually && !displayAsManual &&
                        inspectionStore.inspectionPhotosStore.photosWithIssues >
                          0 && (
                          <Box pr={2}>
                            <ConfirmationButton
                              testId="approve-all"
                              variant="outlined"
                              color="primary"
                        onClick={() => inspectionPhotosStore.overrideLocationValidationForAllPhotos()}
                              confirmDialogProps={{
                                body: `Please confirm if you’d like to manually override and approve the location of all invalid photos.`,
                          confirmButtonText: 'Approve'
                              }}
                            >
                              Approve All
                            </ConfirmationButton>
                          </Box>
                        )}
                      <Box pr={2}>
                        <SelectField
                          options={[
                          {label:'Sort by Tasks', value:SortPhotosBy.TASKS },
                          {label:'Sort by Date', value:SortPhotosBy.DATE },
                          ]}
                          testId={'sortBy'}
                          value={sortBy}
                          onChange={value => setSortBy(value)}
                          fullWidth
                          className={classes.selectSortBy}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Box
                      style={{
                        flexGrow: 1,
                        overflowX: 'hidden',
                      }}
                    >
                      {sortBy === SortPhotosBy.TASKS ? (
                        <>
                          {inspectionTasks.map(task => (
                            <PhotosAccordion
                              key={task.inspectionTaskId}
                              inspectionTaskId={task.inspectionTaskId}
                              title={task.description}
                              isManualUploadMode={displayAsManual}
                              canUploadPhotos={!!isManualUploadMode && isInspectionOpen}
                              inspectionId={inspectionId || inspectionDetails?.inspectionId}
                              ref={el =>
                              (accordionRefs.current[task.inspectionTaskId] = el)
                              }
                            />
                          ))}
                        </>
                      ) : (
                        <Box px={2} py={4}>
                          <PhotoList
                          photos={[...inspectionStore.inspectionPhotosStore.inspectionPhotos]
                          .sort((a, b) => compareDesc(parseDate(a.captureTimeIso), parseDate(b.captureTimeIso)) )}
                          isManualUploadMode={displayAsManual}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                className={clsx(classes.paper, classes.mapContainerPaper)}
                elevation={0}
              >
                {displayAsManual ? (
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '600px',
                    }}
                  >
                    {isPhotoSelected ? (
                      <Box style={{ width: '100%', padding: 16 }}>
                        <PhotoDetail isManualUploadMode={displayAsManual} />
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        Select a photo to see it here
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {isPhotoSelected && (
                      <Slide
                        direction="left"
                        in={isPhotoSelected}
                        mountOnEnter
                        unmountOnExit
                      >
                        <Box
                          style={{
                            width: '100%',
                            padding: 16,
                          }}
                        >
                          <PhotoDetail isManualUploadMode={displayAsManual} />
                        </Box>
                      </Slide>
                    )}
                    {/* hide map instead of not rendering it to avoid map loads to the api */}
                    <Box
                      style={{ height: '600px', width: '100%' }}
                      className={isPhotoSelected ? classes.hiddenMap : null}
                    >
                      <InspectionPhotosMap
                        inspectionDetails={inspectionDetails}
                        inspectionPhotos={inspectionPhotos}
                        displayMapLegend
                        onInspectionPhotoClick={onInspectionPhotoClickHandler}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
        {inspectionStore.isLoading && <VerticalBarLoader />}
        <PickLocationDialog
          open={pickLocationOpen}
          onClose={() => setPickLocationOpen(false)}
        />
        <MovePhotosDialog
          open={movePhotosOpen}
          onClose={() => setMovePhotosOpen(false)}
        />
        <MoveToSectionDialog
          open={moveToSectionOpen}
          onClose={() => setMoveToSectionOpen(false)}
        />
        <SubmitForReviewSourceDialog
          open={submitForReviewSourceOpen}
          onClose={() => setSubmitForReviewSourceOpen(false)}
          onConfirm={photoSourceInfo =>
            inspectionStore.submitForReview(inspectionDetails?.inspectionId, photoSourceInfo, () =>
              inspectionStore.loadInspection(inspectionDetails?.inspectionId)
            )
          }
        />
      </>
    );
  }
);
